import {faClose, faIdCardAlt, faKey} from "@fortawesome/pro-regular-svg-icons";
import {useTranslation} from "react-i18next";
import {SubmitHandler, useForm} from "react-hook-form";
import {useEffect, useMemo, useState} from "react";
import {createFileRoute, useNavigate, useRouter} from "@tanstack/react-router";
import {SxProps} from "@mui/joy/styles/types";
import {Alert, Box, Button, FormControl, FormLabel, IconButton, Input, Stack, Typography, useTheme} from "@mui/joy";
import {autologinTqo, useAuthenticator, useAutologin} from "../auth/authenticator.tsx";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {VersionFooter} from "../VersionFooter.tsx";
import {useQueryClient} from "@tanstack/react-query";
import {ToasterProvider, useBomb} from "../bomb/bomb.tsx";
import {errorize} from "../bomb/errorize.tsx";
import {useGetSystemConfig} from "../rpc/webrpc-client.gen.ts";
import {useMothership} from "../tq/tqx.tsx";

const mainContainerStyle: SxProps = {
    width: "100vw",
    minHeight: "90vh",
    display: "flex",
    flexDirection: "column",
} as const;

const outerboxStyle: SxProps = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flex: 1,
} as const;

interface Inputs {
    username: string;
    password: string;
}

function LoginComponent() {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {history: routerHistory} = useRouter();
    const tqc = useQueryClient();
    const mama = useMothership();
    const auther = useAuthenticator();
    const bomb = useBomb();
    const {data: sysconfData} = useGetSystemConfig();
    const banner = sysconfData?.dratBanner ?? t("login.title");
    useAutologin();
    // const autologin = useAutologin();
    const searchParams = Route.useSearch();
    const goto = searchParams.goto;
    const [loginError, setLoginError] = useState<unknown>(null);
    const {
        register, handleSubmit,
        formState
    } = useForm<Inputs>({
        mode: "onChange",
        defaultValues: {
            username: "",
            password: "",
        }
    });
    const submitDisabled = useMemo(() => {
        return !formState.isDirty || !formState.isValid;
    }, [formState.isDirty, formState.isValid]);
    const onSubmit: SubmitHandler<Inputs> = (data) => {
        const lreq = {
            username: data.username,
            password: data.password,
        };
        mama.login(lreq)
            .then(lrsp => {
                tqc.clear();
                auther.setJwt(lrsp.jwt);
                setTimeout(() => {
                    navigate({to: "/", params: {}}).catch(bomb);
                }, 0);
            })
            .catch((err: unknown) => {
                setLoginError(err);
            });
    };

    // i think this breaks autologin
    //
    // useEffect(() => {
    //     GLOBAL_AUTH_SERVICE.setAuthenticationToken(null);
    // }, []);

    useEffect(() => {
        if (import.meta.env.DEV) {
            // if (!!goto
            //     && auto
            //     && localForageData?.wantsAutologin
            //     && !!localForageData.storedAuthtoken
            //     && !auther.jwt
            // ) {
            //     auther.setJwt(localForageData.storedAuthtoken);
            // }

            if (!!goto && !!auther.getJwt()) {
                routerHistory.push(goto);
            }
        }
    }, [auther, goto, routerHistory]);

    const theme = useTheme();
    const mainColor = theme.palette.primary[900];

    // if (!!goto && !isLocalForageSuccess) {
    //     return <SuspenseDialog message="checking device storage"/>
    // }

    return <ToasterProvider>
        <Box sx={mainContainerStyle}>
            <Box sx={outerboxStyle}>
                {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Stack sx={{
                        padding: "2rem",
                        borderWidth: "4px",
                        borderStyle: "solid",
                        borderColor: "primary.900",
                    }} spacing={2}>
                        <Typography level="h2" sx={{
                            textAlign: "center",
                            color: {mainColor},
                        }}>{banner}</Typography>
                        <FormControl>
                            <FormLabel>{t("login.username")}</FormLabel>
                            <Input
                                startDecorator={<FontAwesomeIcon color={mainColor} icon={faIdCardAlt}/>}
                                {...register("username", {required: true})}
                            />
                        </FormControl>

                        <FormControl>
                            <FormLabel>{t("login.password")}</FormLabel>
                            <Input
                                type="password"
                                startDecorator={<FontAwesomeIcon color={mainColor} icon={faKey}/>}
                                required={true}
                                {...register("password", {required: true})}
                            />
                        </FormControl>

                        <Button disabled={submitDisabled}
                                type="submit"
                                variant="solid"
                                color="primary"
                        >
                            {t("login.login")}
                        </Button>
                    </Stack>

                    <Box sx={{width: "100%"}}>
                        <Alert
                            sx={{display: loginError ? "block" : "none", borderWidth: 4, marginTop: "1rem"}}
                            variant="outlined"
                            color="danger"
                        >
                            <IconButton
                                variant="outlined"
                                aria-label="close"
                                color="danger"
                                sx={{mb: 2}}
                                onClick={() => {
                                    setLoginError(null);
                                }}
                            >
                                <FontAwesomeIcon color="danger" icon={faClose}/>
                            </IconButton>

                            {t("login.failure")}: {errorize(loginError).message}
                        </Alert>
                    </Box>
                </form>
            </Box>
        </Box>
        <VersionFooter/>
    </ToasterProvider>
}

interface LoginSearchParams {
    goto?: string;
}

export const Route
    = createFileRoute("/login")({
    component: LoginComponent,
    validateSearch: (raw: Record<string, unknown>) => {
        const rv: LoginSearchParams = {};
        if (import.meta.env.DEV) {
            if (Object.hasOwn(raw, "goto") && !!raw.goto) {
                rv.goto = raw.goto as string | undefined;
            }
        }
        return rv;
    },
    beforeLoad: ({context, search}) => {
        if (!context.authenticator.getJwt() && search.goto) {
            return context.queryClient.ensureQueryData(autologinTqo)
                .then(({wantsAutologin, storedAuthtoken}) => {
                    if (wantsAutologin && storedAuthtoken) {
                        /* TODO */
                    }
                })
        }
    }
});

